import { FC } from "react";
import styled from "@emotion/styled";
import { InterstitialOverlay } from "@/components/overlay";
import { AuthCardGoogleButtonBase } from "@/components/auth/google/auth-card-google-button-base";
import { MDSLogo, MDSLogoKind } from "@/mds";

interface RequiresSignUpInterstitialOverlayContentProps {
  isOpen: boolean;
  onSignUp: () => void;
  onClose: () => void;
}

export const RequiresSignUpInterstitialOverlayContent: FC<
  RequiresSignUpInterstitialOverlayContentProps
> = ({ isOpen, onSignUp, onClose }) => {
  return (
    <InterstitialOverlay isOpen={isOpen} handleClose={onClose}>
      <StyledContainer>
        <StyledLogo kind={MDSLogoKind.Arrows} />
        <StyledSmallerGap>
          <StyledHeader>Sign up to try all of Mem</StyledHeader>
          <StyledInfo>
            Certain features are limited without an account. Try out all of Mem—the AI notes app
            that keeps you organized.
          </StyledInfo>
        </StyledSmallerGap>
        <StyledSmallerGap>
          <AuthCardGoogleButtonBase label="Sign up with Google" onClick={onSignUp} />
        </StyledSmallerGap>
      </StyledContainer>
    </InterstitialOverlay>
  );
};

const StyledContainer = styled.div`
  align-items: center;
  background: ${({ theme }) => theme.colors.surface.pane};
  border-color: ${({ theme }) => theme.colors.border.default};
  border-radius: 24px;
  border-style: solid;
  border-width: 1px;
  box-shadow: 0px 11px 24px -8px rgba(69, 78, 104, 0.2);
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  flex-direction: column;
  gap: 16px;
  justify-content: flex-start;
  letter-spacing: ${({ theme }) => theme.lineSpacings["header-l"]};
  line-height: 32px;
  max-width: 640px;
  padding: 40px;
  width: 100%;

  @media (min-width: 672px) and (min-height: 632px) {
    justify-content: center;
  }
`;

const StyledHeader = styled.div`
  color: ${({ theme }) => theme.colors.text.primary};
  display: flex;
  font-size: 24px;
  font-weight: 700;
  justify-content: center;
  letter-spacing: ${({ theme }) => theme.lineSpacings["header-l"]};
  line-height: 32px;
  text-align: center;
`;

const StyledInfo = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-weight: 400;
  font-size: 16px;
  letter-spacing: ${({ theme }) => theme.lineSpacings["header-m"]};
  line-height: 24px;
  text-align: center;
`;

const StyledSmallerGap = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const StyledLogo = styled(MDSLogo)`
  height: 56px;
  width: 56px;
`;
